import { apiClient } from './client';

/**
 * 인사기록 정정 요청 API (인사, migration 136 · 시안 B2).
 *
 * 서버: `apps/chat-server/src/modules/hr/profile-requests.routes.ts`.
 *
 * ★★ **승인이 값을 바꾸지 않는다.** 승인은 「맞다, 처리하겠다」 는 대답이고, 응답의
 *    `nextAction` 이 **어디서 처리하는지**를 가리킨다 — 화면은 그 말을 그대로 옮긴다.
 * ★ **요청자는 서버가 본인으로 고정한다** — 이 파일 어디에도 `userId` 를 보내는 자리가 없다.
 * ★ 옛 서버에는 이 라우트가 없다(404). 부르는 쪽이 실패를 삼킨다.
 */

export const REQUEST_FIELDS = [
  'name', 'employeeNumber', 'team', 'position', 'hireDate', 'employmentType', 'OTHER',
] as const;
export type RequestField = (typeof REQUEST_FIELDS)[number];

export const REQUEST_FIELD_LABEL: Record<string, string> = {
  name: '이름', employeeNumber: '사번', team: '부서 · 팀', position: '직위',
  hireDate: '입사일', employmentType: '고용형태', OTHER: '그 밖의 것',
};

export type RequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED';
export const REQUEST_STATUS_LABEL: Record<string, string> = {
  PENDING: '대기', APPROVED: '승인', REJECTED: '반려', CANCELED: '회수',
};

/** 승인 뒤 어디서 처리하나 — 판정은 서버가 한다(화면이 「직위는 발령」 을 갖지 않는다). */
export type NextAction = 'ROSTER' | 'ASSIGNMENT' | 'MANUAL';

export interface ProfileRequestDto {
  id: string;
  userId: string;
  userName: string | null;
  employeeNumber: string | null;
  field: string;
  fieldLabel: string;
  /** 요청한 **그때의** 값 — 지금 값이 아니다. */
  currentValue: string | null;
  requestedValue: string;
  reason: string | null;
  status: RequestStatus;
  statusLabel: string;
  /** 아직 답을 기다리는가. */
  open: boolean;
  nextAction: NextAction;
  nextActionLabel: string;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionNote: string | null;
  createdAt: string;
}

export interface ProfileRequestSummaryDto {
  pending: number;
  approved: number;
  rejected: number;
  /** 대기 중 **발령이 필요한** 건수 — 담당자의 일이 더 무거운 쪽이다. */
  pendingNeedsAssignment: number;
}

export interface ProfileRequestListDto {
  total: number;
  /** 지표는 **거르기와 무관하게 전체**를 본다. */
  summary: ProfileRequestSummaryDto;
  rows: ProfileRequestDto[];
}

export async function listProfileRequests(params: { status?: RequestStatus; search?: string } = {}):
Promise<ProfileRequestListDto> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.search?.trim()) q.set('search', params.search.trim());
  const qs = q.toString();
  const { data } = await apiClient.get<ProfileRequestListDto>(`/hr/profile-requests${qs ? `?${qs}` : ''}`);
  return data;
}

export async function listMyProfileRequests(): Promise<{ rows: ProfileRequestDto[] }> {
  const { data } = await apiClient.get<{ rows: ProfileRequestDto[] }>('/hr/profile-requests/me');
  return data;
}

export interface ProfileRequestInput {
  field: RequestField;
  requestedValue: string;
  /** 화면이 보고 있던 값을 그대로 보낸다 — 나중에 값이 바뀌면 근거가 사라진다. */
  currentValue?: string | null;
  reason?: string | null;
}

export async function createProfileRequest(input: ProfileRequestInput): Promise<ProfileRequestDto> {
  const { data } = await apiClient.post<{ request: ProfileRequestDto }>('/hr/profile-requests', input);
  return data.request;
}

export interface DecideResultDto {
  request: ProfileRequestDto;
  /** ★ **값은 바뀌지 않았다.** 이제 어디서 처리하는지. */
  nextAction: NextAction;
  nextActionLabel: string;
}

/** 승인 · 반려 — **반려는 이유가 있어야 한다**(없으면 서버가 400). */
export async function decideProfileRequest(
  id: string, approve: boolean, note?: string | null,
): Promise<DecideResultDto> {
  const { data } = await apiClient.post<DecideResultDto>(
    `/hr/profile-requests/${id}/decide`, { approve, ...(note ? { note } : {}) },
  );
  return data;
}

/** 회수 — **내가 넣은 대기 중인 것만.** 담당자가 남의 것을 물리는 것은 «반려» 다. */
export async function cancelProfileRequest(id: string): Promise<ProfileRequestDto> {
  const { data } = await apiClient.post<{ request: ProfileRequestDto }>(`/hr/profile-requests/${id}/cancel`);
  return data.request;
}
